import { ArrowDown, FileText } from "lucide-react";
import CountUp from "react-countup";
import { useInView } from "framer-motion";
import { useRef } from "react";

const CV_URL = "/Piyus_Kumar_CV.pdf";

const stats = [
  { value: 2.1, suffix: "M+", label: "Endpoints Secured", decimals: 1 },
  { value: 50, suffix: "+", label: "PoCs Delivered", decimals: 0 },
  { value: 30, suffix: "+", label: "Enterprise Implementations", decimals: 0 },
  { value: 8, suffix: "", label: "Active Certifications", decimals: 0 },
  { value: 25, suffix: "+", label: "Professionals Mentored", decimals: 0 },
  { value: 1, suffix: "", label: "APJ MVP Partner Award", decimals: 0 },
];

const logos = [
  { name: "CrowdStrike", url: "https://cdn.simpleicons.org/crowdstrike/0F172A" },
  { name: "Palo Alto Networks", url: "https://cdn.simpleicons.org/paloaltonetworks/0F172A" },
  { name: "Microsoft", url: "https://cdn.simpleicons.org/microsoft/0F172A" },
  { name: "AWS", url: "https://cdn.simpleicons.org/amazonaws/0F172A" },
  { name: "Proofpoint", url: "https://cdn.simpleicons.org/proofpoint/0F172A" },
  { name: "Qualys", url: "https://cdn.simpleicons.org/qualys/0F172A" },
  { name: "Wiz", url: "https://cdn.simpleicons.org/wiz/0F172A" },
  { name: "Zscaler", url: "https://cdn.simpleicons.org/zscaler/0F172A" },
  { name: "Fortinet", url: "https://cdn.simpleicons.org/fortinet/0F172A" },
];

function StatCounter({ value, suffix, label, decimals }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="stat-number text-2xl sm:text-3xl text-[#0F172A]">
        {isInView ? <CountUp end={value} duration={2.5} decimals={decimals} /> : "0"}
        <span className="text-[#06B6D4]">{suffix}</span>
      </div>
      <p className="text-xs text-[#6B7280] mt-1 font-medium">{label}</p>
    </div>
  );
}

function FloatingLogo({ logo, index }) {
  const delays = [0, 0.3, 0.6, 0.15, 0.45, 0.75, 0.2, 0.5, 0.8];
  const delay = delays[index] || 0;

  return (
    <div
      className="logo-float-card group"
      style={{ animationDelay: `${delay}s` }}
      data-testid={`hero-logo-${logo.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-[90px] h-[90px] rounded-2xl bg-white border border-[#E5E7EB] shadow-sm flex flex-col items-center justify-center gap-2 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg group-hover:border-[#06B6D4]/40 group-hover:shadow-[#06B6D4]/10 cursor-default px-2">
        <img
          src={logo.url}
          alt={logo.name}
          className="w-7 h-7 object-contain opacity-50 group-hover:opacity-90 transition-all duration-300"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span className="text-[9px] font-semibold text-[#9CA3AF] group-hover:text-[#111827] transition-colors leading-tight text-center">{logo.name}</span>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="home" className="relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column */}
          <div className="space-y-6 animate-fadeInUp">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]" data-testid="hero-eyebrow">
              Principal Security Engineer &middot; Security Architect &middot; Pre-Sales Solutions Specialist
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#111827] leading-[1.05]">
              Protecting Enterprises.{" "}
              <span className="text-[#06B6D4]">One Layer</span> at a Time.
            </h1>
            <p className="text-base md:text-lg text-[#6B7280] leading-relaxed max-w-xl">
              5+ years securing enterprise environments with EDR, XDR, SIEM, Identity Protection, and Cloud Security — from PoC to production at scale.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => document.getElementById("expertise")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-explore-btn"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-[#0F172A] text-white font-medium transition-all hover:bg-[#111827] hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
              >
                Explore My Work <ArrowDown className="ml-2 w-4 h-4" />
              </button>
              <a
                href={CV_URL}
                download
                data-testid="hero-cv-btn"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-white text-[#111827] border border-[#E5E7EB] font-medium transition-all hover:border-[#0F172A] hover:bg-[#F9FAFB]"
              >
                <FileText className="mr-2 w-4 h-4" /> Download CV
              </a>
            </div>
            {/* Advisory link */}
            <p className="text-sm text-[#6B7280] pt-1">
              Looking for dedicated advisory services?{" "}
              <a href="https://shiftleftsecure.com" target="_blank" rel="noopener noreferrer" className="text-[#06B6D4] font-medium hover:underline" data-testid="hero-advisory-link">
                shiftleftsecure.com &rarr;
              </a>
            </p>
          </div>

          {/* Right column - Floating Logo Cloud */}
          <div className="relative animate-slideInRight animation-delay-200 hidden lg:block" data-testid="hero-logo-cloud">
            <div className="grid grid-cols-3 gap-4 max-w-[320px] mx-auto">
              {logos.map((logo, i) => (
                <FloatingLogo key={logo.name} logo={logo} index={i} />
              ))}
            </div>
            <p className="text-center text-[11px] text-[#9CA3AF] mt-5 font-medium">
              Platforms I've deployed and operationalized at enterprise scale
            </p>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 mt-16 pt-10 border-t border-[#E5E7EB]" data-testid="hero-stats">
          {stats.map((s) => (
            <StatCounter key={s.label} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}
